'use client';
import React, { useState, useEffect, useRef } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Recording {
  id: string;
  booking_id: string | null;
  client_id: string;
  consultant_id: string;
  title: string;
  description: string | null;
  storage_path: string | null;
  duration_seconds: number;
  file_size_bytes: number;
  thumbnail_url: string | null;
  session_date: string;
  is_available: boolean;
  view_count: number;
  created_at: string;
  // joined
  consultant_name?: string;
  consultant_initials?: string;
  client_name?: string;
  client_initials?: string;
}

type FilterTab = 'all' | 'as_client' | 'as_consultant';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '—';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(0)} MB`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// ─── Video Player Modal ────────────────────────────────────────────────────────
function VideoPlayerModal({
  recording,
  onClose,
}: {
  recording: Recording;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(recording.duration_seconds);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const controlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasRealVideo = !!recording.storage_path;

  const togglePlay = () => {
    if (!videoRef.current || !hasRealVideo) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) setCurrentTime(videoRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = parseFloat(e.target.value);
    setCurrentTime(t);
    if (videoRef.current) videoRef.current.currentTime = t;
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (videoRef.current) videoRef.current.volume = v;
    setIsMuted(v === 0);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    const next = !isMuted;
    setIsMuted(next);
    videoRef.current.muted = next;
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    controlsTimerRef.current = setTimeout(() => setShowControls(false), 3000);
  };

  useEffect(() => {
    return () => {
      if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    };
  }, []);

  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
      <div className="relative w-full max-w-4xl bg-black rounded-2xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-gray-900/80 border-b border-white/10">
          <div className="min-w-0">
            <h3 className="text-white font-600 text-sm truncate">{recording.title}</h3>
            <p className="text-gray-400 text-xs mt-0.5">{formatDate(recording.session_date)}</p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
          >
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>

        {/* Video area */}
        <div
          className="relative bg-black aspect-video flex items-center justify-center cursor-pointer"
          onMouseMove={handleMouseMove}
          onClick={togglePlay}
        >
          {hasRealVideo ? (
            <video
              ref={videoRef}
              src={recording.storage_path!}
              className="w-full h-full object-contain"
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={() => setIsPlaying(false)}
            />
          ) : (
            /* Placeholder when no real video file */
            <div className="flex flex-col items-center gap-4 text-center px-8">
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center">
                <Icon name="FilmIcon" size={40} className="text-primary" />
              </div>
              <div>
                <p className="text-white font-600 text-lg">{recording.title}</p>
                <p className="text-gray-400 text-sm mt-1">
                  Recording preview — {formatDuration(recording.duration_seconds)}
                </p>
                <p className="text-gray-500 text-xs mt-3">
                  Video file will appear here once the recording is uploaded to storage.
                </p>
              </div>
            </div>
          )}

          {/* Play/Pause overlay */}
          {hasRealVideo && showControls && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center">
                <Icon name={isPlaying ? 'PauseIcon' : 'PlayIcon'} size={32} className="text-white" />
              </div>
            </div>
          )}
        </div>

        {/* Controls bar */}
        <div className="px-5 py-3 bg-gray-900 space-y-2">
          {/* Progress */}
          <div className="flex items-center gap-3">
            <span className="text-gray-400 text-xs w-10 text-right tabular-nums">
              {formatDuration(Math.floor(currentTime))}
            </span>
            <input
              type="range"
              min={0}
              max={duration}
              step={1}
              value={currentTime}
              onChange={handleSeek}
              disabled={!hasRealVideo}
              className="flex-1 h-1.5 accent-primary cursor-pointer disabled:opacity-40"
            />
            <span className="text-gray-400 text-xs w-10 tabular-nums">
              {formatDuration(Math.floor(duration))}
            </span>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={togglePlay}
              disabled={!hasRealVideo}
              className="p-2 rounded-lg text-white hover:bg-white/10 transition-colors disabled:opacity-40"
            >
              <Icon name={isPlaying ? 'PauseIcon' : 'PlayIcon'} size={20} />
            </button>
            <button
              onClick={toggleMute}
              disabled={!hasRealVideo}
              className="p-2 rounded-lg text-white hover:bg-white/10 transition-colors disabled:opacity-40"
            >
              <Icon name={isMuted ? 'SpeakerXMarkIcon' : 'SpeakerWaveIcon'} size={20} />
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              disabled={!hasRealVideo}
              className="w-20 h-1.5 accent-primary cursor-pointer disabled:opacity-40"
            />
            <div className="flex-1" />
            <span className="text-gray-400 text-xs">
              {formatFileSize(recording.file_size_bytes)}
            </span>
            <span className="text-gray-500 text-xs">•</span>
            <span className="text-gray-400 text-xs">{recording.view_count} views</span>
          </div>
        </div>

        {/* Description */}
        {recording.description && (
          <div className="px-5 py-3 bg-gray-900 border-t border-white/10">
            <p className="text-gray-400 text-sm">{recording.description}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Recording Card ────────────────────────────────────────────────────────────
function RecordingCard({
  recording,
  currentUserId,
  onPlay,
}: {
  recording: Recording;
  currentUserId: string;
  onPlay: (r: Recording) => void;
}) {
  const isConsultant = recording.consultant_id === currentUserId;
  const otherPartyName = isConsultant
    ? (recording.client_name ?? 'Client')
    : (recording.consultant_name ?? 'Consultant');
  const otherPartyInitials = isConsultant
    ? (recording.client_initials ?? 'CL')
    : (recording.consultant_initials ?? 'CO');

  return (
    <div className="bg-card border border-border rounded-2xl p-5 hover:shadow-card transition-shadow duration-200 flex flex-col gap-4">
      {/* Top row */}
      <div className="flex items-start gap-4">
        {/* Thumbnail / avatar */}
        <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 relative overflow-hidden">
          {recording.thumbnail_url ? (
            <img
              src={recording.thumbnail_url}
              alt={`Thumbnail for ${recording.title}`}
              className="w-full h-full object-cover"
            />
          ) : (
            <Icon name="FilmIcon" size={28} className="text-primary" />
          )}
          {/* Duration badge */}
          <div className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-1 py-0.5 rounded font-500">
            {formatDuration(recording.duration_seconds)}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-600 text-foreground text-sm leading-snug line-clamp-2">
            {recording.title}
          </h3>
          <div className="flex items-center gap-2 mt-1.5">
            <div className="w-5 h-5 rounded-full gradient-teal flex items-center justify-center text-white text-[9px] font-700 flex-shrink-0">
              {otherPartyInitials}
            </div>
            <span className="text-xs text-muted-foreground truncate">{otherPartyName}</span>
            <span className="text-muted-foreground/40 text-xs">•</span>
            <span
              className={`text-[10px] font-600 px-1.5 py-0.5 rounded-full ${
                isConsultant
                  ? 'bg-blue-500/10 text-blue-600' :'bg-emerald-500/10 text-emerald-600'
              }`}
            >
              {isConsultant ? 'As Consultant' : 'As Client'}
            </span>
          </div>
        </div>
      </div>

      {/* Description */}
      {recording.description && (
        <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
          {recording.description}
        </p>
      )}

      {/* Meta row */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <Icon name="CalendarDaysIcon" size={13} />
          <span>{formatDate(recording.session_date)}</span>
        </div>
        <div className="flex items-center gap-1">
          <Icon name="EyeIcon" size={13} />
          <span>{recording.view_count} views</span>
        </div>
        <div className="flex items-center gap-1">
          <Icon name="CircleStackIcon" size={13} />
          <span>{formatFileSize(recording.file_size_bytes)}</span>
        </div>
      </div>

      {/* Action */}
      <button
        onClick={() => onPlay(recording)}
        disabled={!recording.is_available}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-600 hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Icon name="PlayIcon" size={16} />
        {recording.is_available ? 'Watch Recording' : 'Unavailable'}
      </button>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────
export default function ConsultationRecordingsPage() {
  const supabase = createClient();

  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [playingRecording, setPlayingRecording] = useState<Recording | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string>('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        const uid = user?.id ?? '';
        setCurrentUserId(uid);

        const { data, error: fetchError } = await supabase
          .from('consultation_recordings')
          .select('*')
          .or(`client_id.eq.${uid},consultant_id.eq.${uid}`)
          .order('session_date', { ascending: false });

        if (fetchError) throw fetchError;

        // Enrich with user names from user_profiles
        const enriched: Recording[] = await Promise.all(
          (data ?? []).map(async (rec: Recording) => {
            let consultant_name = 'Consultant';
            let consultant_initials = 'CO';
            let client_name = 'Client';
            let client_initials = 'CL';

            const { data: consultantProfile } = await supabase
              .from('user_profiles')
              .select('full_name')
              .eq('id', rec.consultant_id)
              .single();

            if (consultantProfile?.full_name) {
              consultant_name = consultantProfile.full_name;
              consultant_initials = getInitials(consultantProfile.full_name);
            }

            const { data: clientProfile } = await supabase
              .from('user_profiles')
              .select('full_name')
              .eq('id', rec.client_id)
              .single();

            if (clientProfile?.full_name) {
              client_name = clientProfile.full_name;
              client_initials = getInitials(clientProfile.full_name);
            }

            return { ...rec, consultant_name, consultant_initials, client_name, client_initials };
          })
        );

        setRecordings(enriched);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load recordings');
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

  const filtered = recordings.filter((r) => {
    const matchesTab =
      activeTab === 'all' ||
      (activeTab === 'as_client' && r.client_id === currentUserId) ||
      (activeTab === 'as_consultant' && r.consultant_id === currentUserId);

    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      r.title.toLowerCase().includes(q) ||
      (r.description ?? '').toLowerCase().includes(q) ||
      (r.consultant_name ?? '').toLowerCase().includes(q) ||
      (r.client_name ?? '').toLowerCase().includes(q);

    return matchesTab && matchesSearch;
  });

  const asClientCount = recordings.filter((r) => r.client_id === currentUserId).length;
  const asConsultantCount = recordings.filter((r) => r.consultant_id === currentUserId).length;
  const totalDuration = recordings.reduce((acc, r) => acc + r.duration_seconds, 0);

  const tabs: { key: FilterTab; label: string; count: number }[] = [
    { key: 'all', label: 'All Recordings', count: recordings.length },
    { key: 'as_client', label: 'As Client', count: asClientCount },
    { key: 'as_consultant', label: 'As Consultant', count: asConsultantCount },
  ];

  return (
    <AppLayout activeRoute="/consultation-recordings">
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-700 text-foreground">Consultation Recordings</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Review past sessions — watch as a client or rewatch as a consultant
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Recordings', value: recordings.length, icon: 'FilmIcon', color: 'text-primary' },
            { label: 'As Client', value: asClientCount, icon: 'UserIcon', color: 'text-emerald-600' },
            { label: 'As Consultant', value: asConsultantCount, icon: 'BriefcaseIcon', color: 'text-blue-600' },
            { label: 'Total Duration', value: formatDuration(totalDuration), icon: 'ClockIcon', color: 'text-amber-600' },
          ].map((stat) => (
            <div key={stat.label} className="bg-card border border-border rounded-2xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={20} className={stat.color} />
              </div>
              <div className="min-w-0">
                <p className="text-lg font-700 text-foreground">{stat.value}</p>
                <p className="text-xs text-muted-foreground truncate">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Tabs */}
          <div className="flex gap-1 bg-muted rounded-xl p-1 flex-shrink-0">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-500 transition-all duration-150 ${
                  activeTab === tab.key
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full font-600 ${
                    activeTab === tab.key ? 'bg-primary/10 text-primary' : 'bg-muted-foreground/20 text-muted-foreground'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <Icon
              name="MagnifyingGlassIcon"
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              placeholder="Search recordings…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-card border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card border border-border rounded-2xl p-5 animate-pulse space-y-3">
                <div className="flex gap-4">
                  <div className="w-14 h-14 rounded-xl bg-muted" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </div>
                <div className="h-3 bg-muted rounded w-full" />
                <div className="h-3 bg-muted rounded w-2/3" />
                <div className="h-10 bg-muted rounded-xl" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="bg-destructive/10 border border-destructive/20 rounded-2xl p-6 text-center">
            <Icon name="ExclamationTriangleIcon" size={32} className="text-destructive mx-auto mb-2" />
            <p className="text-destructive font-600 text-sm">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 text-xs text-muted-foreground hover:text-foreground underline"
            >
              Try again
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-card border border-border rounded-2xl p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Icon name="FilmIcon" size={32} className="text-muted-foreground" />
            </div>
            <h3 className="font-600 text-foreground text-base">No recordings found</h3>
            <p className="text-muted-foreground text-sm mt-1 max-w-xs mx-auto">
              {searchQuery
                ? 'No recordings match your search. Try different keywords.'
                : 'Recordings from your completed consultation sessions will appear here.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((rec) => (
              <RecordingCard
                key={rec.id}
                recording={rec}
                currentUserId={currentUserId}
                onPlay={setPlayingRecording}
              />
            ))}
          </div>
        )}
      </div>

      {/* Video Player Modal */}
      {playingRecording && (
        <VideoPlayerModal
          recording={playingRecording}
          onClose={() => setPlayingRecording(null)}
        />
      )}
    </AppLayout>
  );
}
